import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const STORAGE_KEY = 'annual_promo_dismissed_v1';

/**
 * Dashboard promo banner that nudges active monthly subscribers to switch
 * to annual billing (saves $70/year = ~2 months free). Self-dismissible
 * and only renders for users with an active membership.
 */
export function AnnualPromoBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    setDismissed(localStorage.getItem(STORAGE_KEY) === '1');
  }, []);

  if (dismissed) return null;
  const isActive = user?.membership_status === 'active';
  if (!isActive) return null;

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      className="mt-4 p-3 sm:p-4 bg-gradient-to-r from-[#EFF6FF] via-white to-[#EFF6FF] border border-[#0047AB]/30 rounded-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3 relative"
      data-testid="annual-promo-banner"
    >
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-8 h-8 rounded-sm bg-[#0047AB] text-white flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">Switch to annual — save $70/year</p>
          <p className="text-xs text-slate-600 mt-0.5">
            Pay $350/year instead of $420 (12 × $35) — that's 2 months free.
            Use code <span className="font-mono font-semibold text-[#0047AB]">ANNUAL50</span> to save an extra $50 on top.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Link to="/billing?cycle=annual" data-testid="annual-promo-cta">
          <span className="inline-flex items-center gap-1 bg-[#0047AB] hover:bg-[#003580] text-white text-xs font-medium px-3 py-2 rounded-sm transition-colors">
            Switch to annual <ArrowRight className="w-3 h-3" />
          </span>
        </Link>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="p-1 text-slate-400 hover:text-slate-600"
          data-testid="annual-promo-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
